/**
 * Development-only auth bypass for testing UI without Azure Entra ID.
 * Activate by setting VITE_DEV_AUTH_BYPASS=true in .env.local.
 *
 * Seeds React Query cache with mock data so the app renders a realistic
 * profile list, categories, and relationship levels — enough to test
 * layout and viewport behavior on real devices or the iOS Simulator.
 */
import type { QueryClient } from "@tanstack/react-query";
import type { UserDataRes } from "@/types/user";
import type { ProfileListRes, ProfileRes } from "@/types/profile";
import type { CategoryRes } from "@/types/category";
import type { RelationshipLevelRes } from "@api/relationshipLevel";
import type { OpportunityCategory } from "@api/opportunity";

export const DEV_AUTH_BYPASS =
  import.meta.env.VITE_DEV_AUTH_BYPASS === "true";

const NAMES = [
  "Aaron Attarzadeh", "Aaron Barkley", "Aaron Batson", "Aaron Bierling",
  "Adam Chen", "Alex Rodriguez", "Amanda Foster", "Andrew Kim",
  "Angela Wright", "Barbara Johnson", "Ben Thompson", "Brian Lee",
  "Carol Martinez", "Chris Wilson", "Dana Brown", "David Garcia",
  "Emily Davis", "Frank Miller", "Grace Taylor", "Hannah Anderson",
  "Ian Cooper", "Jackie Nguyen", "Karen White", "Larry Scott",
  "Maria Lopez", "Nathan Hall", "Olivia Young", "Patrick King",
  "Quinn Adams", "Rachel Green",
];

const ROLES = ["Lighting Designer", "Other", "Gaffer", "Technician", "Sales Rep"];
const MARKETS = ["Concert, Theater", "Corporate", "Film", "Broadcast", "Touring"];
const REPS = ["Prospects", "Montreal", "Toronto", "Nashville", "Chicago"];
const LEVELS = [1, 2, 3, 4, 5, 6, 7, null];

function mockProfiles(count: number): ProfileRes[] {
  return Array.from({ length: count }, (_, i) => ({
    id: `mock-${i}`,
    name: NAMES[i % NAMES.length],
    roles_ids: [`role-${i % 5}`],
    markets_ids: [`market-${i % 5}`],
    reps_ids: [`rep-${i % 5}`],
    locations_ids: [],
    relationship_level: LEVELS[i % LEVELS.length],
  }));
}

const mockUserData: UserDataRes = {
  id: "mock-user",
  full_name: "Dev Tester",
  preferences: {},
  roles: ["WikiAdmin"],
  isAdmin: true,
};

const mockCategories: CategoryRes = [
  {
    id: "cat-role", name: "Role",
    items: ROLES.map((r, i) => ({ id: `role-${i}`, name: r, count: 20 })),
  },
  {
    id: "cat-market", name: "Market",
    items: MARKETS.map((m, i) => ({ id: `market-${i}`, name: m, count: 20 })),
  },
  {
    id: "cat-location", name: "Location",
    items: [
      { id: "loc-0", name: "New York", count: 15 },
      { id: "loc-1", name: "Los Angeles", count: 12 },
      { id: "loc-2", name: "Chicago", count: 10 },
    ],
  },
  {
    id: "cat-rep", name: "Rep",
    items: REPS.map((r, i) => ({ id: `rep-${i}`, name: r, count: 20 })),
  },
];

const mockRelationshipLevels: RelationshipLevelRes[] = Array.from(
  { length: 7 },
  (_, i) => ({
    level: i + 1,
    title: ["Dormant", "Cool", "Warm", "Finalist", "Serious", "Regular", "VIP"][i],
    description: `Level ${i + 1}`,
  })
);

const mockOpportunityCategories: OpportunityCategory[] = [
  {
    id: "opp-cat-1", name: "Rental", sort_order: 0,
    subcategories: [
      { id: "sub-1", name: "Short Term", category_id: "opp-cat-1", sort_order: 0 },
      { id: "sub-2", name: "Long Term", category_id: "opp-cat-1", sort_order: 1 },
    ],
    columns: [
      { id: "col-1", field_key: "client", header: "Client", field_type: "text", is_primary_sort_field: false, sort_order: 0 },
      { id: "col-2", field_key: "start_date", header: "Start Date", field_type: "date", is_primary_sort_field: true, sort_order: 1 },
      { id: "col-3", field_key: "venue", header: "Venue", field_type: "text", is_primary_sort_field: false, sort_order: 2 },
    ],
  },
  {
    id: "opp-cat-2", name: "Sales", sort_order: 1,
    subcategories: [
      { id: "sub-3", name: "New Business", category_id: "opp-cat-2", sort_order: 0 },
    ],
    columns: [
      { id: "col-4", field_key: "company", header: "Company", field_type: "text", is_primary_sort_field: true, sort_order: 0 },
      { id: "col-5", field_key: "close_date", header: "Close Date", field_type: "date", is_primary_sort_field: false, sort_order: 1 },
    ],
  },
  {
    id: "opp-cat-3", name: "Service", sort_order: 2,
    subcategories: [],
    columns: [
      { id: "col-6", field_key: "description", header: "Description", field_type: "text", is_primary_sort_field: false, sort_order: 0 },
    ],
  },
];

const mockProfileList: ProfileListRes = {
  data: mockProfiles(30),
  count: 120,
};

export function seedQueryCache(queryClient: QueryClient): void {
  if (!DEV_AUTH_BYPASS) return;

  console.warn("[DEV AUTH BYPASS] Seeding query cache with mock data");

  queryClient.setQueryData(["userData"], mockUserData);
  queryClient.setQueryData(["categories"], mockCategories);
  queryClient.setQueryData(["relationshipLevels"], mockRelationshipLevels);

  queryClient.setQueryData(["opportunityCategories"], mockOpportunityCategories);

  // InfiniteQuery needs pages/pageParams structure
  queryClient.setQueryData(["profileList", {}], {
    pages: [mockProfileList],
    pageParams: [0],
  });
}
